import React,{useState,useEffect} from 'react';
import {openTemplatedPrintWindow} from '../utils/printUtils';
import Layout from '../components/layout/Layout';
import SlidePanel from '../components/common/SlidePanel';
import Badge from '../components/common/Badge';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import api from '../services/api';
import {fmtDateTime} from '../utils/helpers';

const SPECIMEN_TYPES=[
  'Cervical Biopsy','Endometrial Biopsy','Skin Biopsy','Breast Lump Excision','Lymph Node Biopsy',
  'Colonic Biopsy','Gastric Biopsy','Liver Biopsy','Renal Biopsy','Prostate Biopsy','Bone Marrow Biopsy',
  'FNAC — Thyroid','FNAC — Breast','FNAC — Lymph Node','Pap Smear','Pleural Fluid Cytology','Ascitic Fluid Cytology',
  'Urine Cytology','Sputum Cytology','CSF Cytology','Other',
];

const REPORT_TYPE=(specimen:string)=>specimen.startsWith('FNAC')||specimen.includes('Cytology')||specimen==='Pap Smear'?'CYTOLOGY':'HISTOPATHOLOGY';

export default function LabPathologyReportPage(){
  const [reports,setReports]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const [showForm,setShowForm]=useState(false);
  const [patient,setPatient]=useState<any>(null);
  const [patientOrders,setPatientOrders]=useState<any[]>([]);
  const [ordersLoading,setOrdersLoading]=useState(false);
  const [selected,setSelected]=useState<any>(null);
  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  const defaultForm={requestId:'',specimenType:'Cervical Biopsy',grossFindings:'',microscopicFindings:'',diagnosis:'',impression:''};
  const [form,setForm]=useState<any>({...defaultForm});

  const loadData=async()=>{
    setLoading(true);
    try{
      const r=await api.getAllHistopathology?.().catch(()=>({data:[]}));
      setReports(Array.isArray(r?.data)?r.data:[]);
    }catch{}
    setLoading(false);
  };
  useEffect(()=>{loadData();},[]);

  const onPatientSelect=async(p:any)=>{
    setPatient(p);setForm(f=>({...f,requestId:''}));
    setOrdersLoading(true);
    try{
      const r=await api.getPatientLabOrders?.(p.id).catch(()=>({data:[]}));
      setPatientOrders(Array.isArray(r?.data)?r.data:[]);
    }catch{setPatientOrders([]);}
    setOrdersLoading(false);
  };

  const saveReport=async()=>{
    if(!form.requestId){notify('❌ Select the lab order this report belongs to');return;}
    if(!form.diagnosis){notify('❌ Diagnosis is required');return;}
    setSaving(true);
    try{
      await api.saveHistopathology?.(form);
      notify('✅ Pathology report saved!');setShowForm(false);await loadData();
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    setSaving(false);
  };

  const printPathology=async(r:any)=>{
    const specimen=r.specimen_type||r.specimenType||'';
    const reportType=REPORT_TYPE(specimen);
    const isCytology=reportType==='CYTOLOGY';
    const contentHtml=`
      <div class="section">
        <div class="row">
          <div class="field"><div class="lbl">Patient</div><div class="val">${r.patient_first_name||''} ${r.patient_last_name||''} (${r.uhid||'—'})</div></div>
          <div class="field"><div class="lbl">Specimen</div><div class="val">${specimen}</div></div>
        </div>
        <div class="row">
          <div class="field"><div class="lbl">Order No.</div><div class="val">${r.request_number||'—'}</div></div>
          <div class="field"><div class="lbl">Reported</div><div class="val">${r.reported_at?fmtDateTime(r.reported_at):'Pending'}</div></div>
        </div>
      </div>
      ${r.gross_findings||r.grossFindings?`<h3 class="section-title">Gross Findings</h3><p class="data-val">${r.gross_findings||r.grossFindings}</p>`:''}
      ${r.microscopic_findings||r.microscopicFindings?`<h3 class="section-title">Microscopic Findings</h3><p class="data-val">${r.microscopic_findings||r.microscopicFindings}</p>`:''}
      <h3 class="section-title">Diagnosis</h3>
      <div style="background:#fef3c7;border-radius:6px;padding:10px 14px;font-weight:bold;font-size:14px;">${r.diagnosis||'—'}</div>
      ${r.impression?`<h3 class="section-title">Impression</h3><p class="data-val">${r.impression}</p>`:''}
    `;
    await openTemplatedPrintWindow({
      api, contentHtml, docTitle:`${isCytology?'Cytology':'Histopathology'} Report`, reportType,
      department:isCytology?'Cytology':'Histopathology', barcodeText:r.request_number||r.id,
      noteText:'This report is generated by the Laboratory Information System. Correlate clinically.',
    });
  };

  return(
    <Layout title="Pathology Reports" actions={
      <button onClick={()=>{setForm({...defaultForm});setPatient(null);setPatientOrders([]);setShowForm(true);}} className="btn-primary">🔬 New Pathology Report</button>
    }>
      {msg&&<div className="fixed top-4 right-4 bg-white border shadow-xl rounded-xl px-4 py-3 text-sm z-50">{msg}</div>}

      <div className="space-y-4">
        <p className="text-sm text-slate-500">Histopathology and cytology results — gross/microscopic findings, diagnosis, and impression.</p>
        <div className="card overflow-hidden">
          <div className="p-3 bg-slate-50 border-b grid grid-cols-7 text-xs font-semibold text-slate-500 uppercase gap-2">
            <span className="col-span-2">Patient</span><span className="col-span-2">Specimen</span><span>Type</span><span>Reported</span><span>Actions</span>
          </div>
          {loading?<div className="p-8 text-center text-slate-400">Loading...</div>
          :reports.length===0?<div className="p-8 text-center text-slate-400">No pathology reports yet. Click "+ New Pathology Report" to add one.</div>
          :reports.map((r:any)=>(
            <div key={r.id} className="grid grid-cols-7 gap-2 p-3 items-center hover:bg-slate-50 border-b text-sm">
              <div className="col-span-2"><p className="font-semibold">{r.patient_first_name||''} {r.patient_last_name||''}</p><p className="text-xs font-mono text-[#02357A]">{r.uhid||'—'}</p></div>
              <span className="col-span-2 truncate">{r.specimen_type}</span>
              <Badge label={REPORT_TYPE(r.specimen_type||'')} className={REPORT_TYPE(r.specimen_type||'')==='CYTOLOGY'?'bg-purple-100 text-purple-700':'bg-blue-100 text-[#02357A]'}/>
              <span className="text-xs text-slate-400">{r.reported_at?fmtDateTime(r.reported_at):'Pending'}</span>
              <div className="flex gap-1">
                <button onClick={()=>setSelected(r)} className="text-xs text-[#02357A] hover:underline">View</button>
                <button onClick={()=>printPathology(r)} className="text-xs text-slate-500 hover:underline">🖨️</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* New Report Panel */}
      <SlidePanel isOpen={showForm} onClose={()=>setShowForm(false)} title="New Pathology Report" icon="🔬" size="xl"
        actions={<button onClick={saveReport} disabled={saving} className="btn-primary w-full">{saving?'Saving...':'Save Pathology Report'}</button>}>
        <div className="space-y-4">
          <PatientSearchInput label="Patient *" selected={patient} onSelect={onPatientSelect} onClear={()=>{setPatient(null);setPatientOrders([]);setForm(f=>({...f,requestId:''}));}}/>

          {patient&&(
            <div>
              <label className="label">Lab Order *</label>
              {ordersLoading?<p className="text-xs text-slate-400">Loading orders...</p>
              :patientOrders.length===0?(
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
                  No lab orders found for this patient. A lab order must exist first — create one from Laboratory → New Order (specimen sent for {form.specimenType.includes('FNAC')||form.specimenType.includes('Cytology')||form.specimenType==='Pap Smear'?'cytology':'histopathology'}), then come back here.
                </div>
              ):(
                <select className="input" value={form.requestId} onChange={e=>setForm({...form,requestId:e.target.value})}>
                  <option value="">Select the lab order this specimen belongs to...</option>
                  {patientOrders.map((o:any)=>(
                    <option key={o.id} value={o.id}>{o.requestNumber||o.request_number} — {(o.tests||[]).map((t:any)=>t.testName).join(', ')||'—'} ({o.status})</option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div><label className="label">Specimen Type *</label>
            <select className="input" value={form.specimenType} onChange={e=>setForm({...form,specimenType:e.target.value})}>
              {SPECIMEN_TYPES.map(s=><option key={s}>{s}</option>)}
            </select>
            <p className="text-xs text-slate-400 mt-1">Report type: <span className="font-semibold">{REPORT_TYPE(form.specimenType)}</span></p>
          </div>
          <div><label className="label">Gross Findings</label><textarea className="input w-full" rows={3} value={form.grossFindings} onChange={e=>setForm({...form,grossFindings:e.target.value})} placeholder="Macroscopic description of the specimen..."/></div>
          <div><label className="label">Microscopic Findings</label><textarea className="input w-full" rows={4} value={form.microscopicFindings} onChange={e=>setForm({...form,microscopicFindings:e.target.value})} placeholder="Microscopic examination details..."/></div>
          <div><label className="label">Diagnosis *</label><textarea className="input w-full" rows={2} value={form.diagnosis} onChange={e=>setForm({...form,diagnosis:e.target.value})} placeholder="Final diagnosis..."/></div>
          <div><label className="label">Impression</label><textarea className="input w-full" rows={2} value={form.impression} onChange={e=>setForm({...form,impression:e.target.value})} placeholder="Clinical impression / recommendation..."/></div>
        </div>
      </SlidePanel>

      {/* View Report */}
      <SlidePanel isOpen={!!selected} onClose={()=>setSelected(null)} title={`Pathology — ${selected?.specimen_type||''}`} icon="🔬" size="lg">
        {selected&&(
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              {[['Patient',`${selected.patient_first_name||''} ${selected.patient_last_name||''}`],['UHID',selected.uhid||'—'],['Specimen',selected.specimen_type],['Order No.',selected.request_number||'—']].map(([l,v])=>(
                <div key={String(l)} className="bg-slate-50 rounded p-2"><p className="text-xs text-slate-400">{l}</p><p className="font-medium">{v}</p></div>
              ))}
            </div>
            {selected.gross_findings&&<div className="bg-slate-50 rounded p-3"><p className="text-xs text-slate-400 mb-1">Gross Findings</p><p>{selected.gross_findings}</p></div>}
            {selected.microscopic_findings&&<div className="bg-slate-50 rounded p-3"><p className="text-xs text-slate-400 mb-1">Microscopic Findings</p><p>{selected.microscopic_findings}</p></div>}
            <div className="bg-amber-50 border border-amber-200 rounded p-3"><p className="text-xs text-amber-600 mb-1">Diagnosis</p><p className="font-bold">{selected.diagnosis||'—'}</p></div>
            {selected.impression&&<div className="bg-slate-50 rounded p-3"><p className="text-xs text-slate-400 mb-1">Impression</p><p>{selected.impression}</p></div>}
            <button onClick={()=>printPathology(selected)} className="btn-primary w-full">🖨️ Print Pathology Report</button>
          </div>
        )}
      </SlidePanel>
    </Layout>
  );
}
